import type { BundlePairInfo } from "@smartmed/codes";

/** Works on any string key (codes or ids) as long as `present` and `bundlePairs` use the same one. */
export const findOrphanedAsterisks = (args: {
  present: ReadonlySet<string>;
  bundlePairs: readonly BundlePairInfo[];
}) => {
  const { present, bundlePairs } = args;
  const daggersByAsterisk = new Map<string, string[]>();
  for (const { daggerCode, asteriskCode } of bundlePairs) {
    if (!present.has(asteriskCode)) continue;
    daggersByAsterisk.set(asteriskCode, [...(daggersByAsterisk.get(asteriskCode) ?? []), daggerCode]);
  }

  return [...daggersByAsterisk.entries()]
    .filter(([, daggers]) => !daggers.some((dagger) => present.has(dagger)))
    .map(([asterisk]) => asterisk);
};

export type SoleAsteriskToAdd = { daggerCode: string; asteriskCode: string };

/** Every present dagger with exactly one registered asterisk that is not present. `bundlePairs` must hold every pair of each present dagger, or a dagger with several asterisks reads as having one. */
export const findDaggersMissingSoleAsterisk = (args: {
  codesInSequence: readonly { code: string }[];
  bundlePairs: readonly BundlePairInfo[];
}): SoleAsteriskToAdd[] => {
  const { codesInSequence, bundlePairs } = args;
  const present = new Set(codesInSequence.map(({ code }) => code));
  const asterisksByDagger = new Map<string, BundlePairInfo[]>();
  for (const pair of bundlePairs) {
    asterisksByDagger.set(pair.daggerCode, [...(asterisksByDagger.get(pair.daggerCode) ?? []), pair]);
  }

  return codesInSequence.flatMap(({ code }): SoleAsteriskToAdd[] => {
    const asterisks = asterisksByDagger.get(code) ?? [];
    if (asterisks.length !== 1) return [];

    const [{ asteriskCode }] = asterisks;
    return present.has(asteriskCode) ? [] : [{ daggerCode: code, asteriskCode }];
  });
};

/** The asterisks to add: one per asterisk, naming the first dagger in sequence that needs it. */
export const findSoleAsterisksToAdd = (args: {
  codesInSequence: readonly { code: string }[];
  bundlePairs: readonly BundlePairInfo[];
}): SoleAsteriskToAdd[] => {
  const missing = findDaggersMissingSoleAsterisk(args);
  return missing.filter(
    (pair, index) => missing.findIndex(({ asteriskCode }) => asteriskCode === pair.asteriskCode) === index,
  );
};

export type DaggerAsteriskGlueResult<T> = {
  codesInSequence: T[];
  orphanedAsterisks: T[];
  /** Set only when gluing would have dropped these codes; `codesInSequence` is then the input order. */
  lostCodes?: string[];
};

const countByCode = (items: readonly { code: string }[]) =>
  items.reduce((counts, { code }) => counts.set(code, (counts.get(code) ?? 0) + 1), new Map<string, number>());

const findLostCodes = (input: readonly { code: string }[], output: readonly { code: string }[]) => {
  const outputCounts = countByCode(output);
  return [...countByCode(input)].filter(([code, count]) => (outputCounts.get(code) ?? 0) < count).map(([code]) => code);
};

/**
 * The primary (index 0) never moves. A dagger is duplicated once per extra asterisk.
 * Pairing is decided in one pass up front, not by a consume-as-you-go walk.
 */
export const glueDaggerAsteriskPairs = <T extends { code: string }>(args: {
  codesInSequence: readonly T[];
  bundlePairs: readonly BundlePairInfo[];
}): DaggerAsteriskGlueResult<T> => {
  const { codesInSequence, bundlePairs } = args;
  if (codesInSequence.length === 0) return { codesInSequence: [], orphanedAsterisks: [] };

  const byCode = new Map(codesInSequence.map((item) => [item.code, item]));
  const presentCodes = new Set(byCode.keys());

  const daggersByAsterisk = new Map<string, string[]>();
  const asterisksByDagger = new Map<string, string[]>();
  for (const { daggerCode, asteriskCode } of bundlePairs) {
    if (presentCodes.has(asteriskCode)) {
      daggersByAsterisk.set(asteriskCode, [...(daggersByAsterisk.get(asteriskCode) ?? []), daggerCode]);
    }
    if (presentCodes.has(daggerCode) && presentCodes.has(asteriskCode)) {
      asterisksByDagger.set(daggerCode, [...(asterisksByDagger.get(daggerCode) ?? []), asteriskCode]);
    }
  }

  const orphanedAsterisks = findOrphanedAsterisks({ present: presentCodes, bundlePairs }).map(
    (asteriskCode) => byCode.get(asteriskCode)!,
  );

  const buildClusterAfter = (anchor: T, asteriskCodes: readonly string[]): T[] => {
    const cluster: T[] = [anchor];
    asteriskCodes.forEach((asteriskCode, i) => {
      if (i > 0) cluster.push({ ...anchor });
      cluster.push(byCode.get(asteriskCode)!);
    });
    return cluster;
  };

  const primary = codesInSequence[0];
  const primaryDagger = (daggersByAsterisk.get(primary.code) ?? []).find((code) => presentCodes.has(code));

  // Codes claimed by a cluster; skipped when emitted standalone.
  const relocated = new Set<string>();
  let primaryCluster: T[];
  if (primaryDagger) {
    relocated.add(primaryDagger);
    const otherAsterisks = (asterisksByDagger.get(primaryDagger) ?? []).filter((code) => code !== primary.code);
    otherAsterisks.forEach((code) => relocated.add(code));
    primaryCluster = [primary, ...buildClusterAfter(byCode.get(primaryDagger)!, otherAsterisks)];
  } else {
    const primaryAsterisks = asterisksByDagger.get(primary.code) ?? [];
    primaryAsterisks.forEach((code) => relocated.add(code));
    primaryCluster = buildClusterAfter(primary, primaryAsterisks);
  }

  const clusterAsterisksByDagger = new Map<string, string[]>();
  for (const code of presentCodes) {
    if (code === primary.code || code === primaryDagger) continue;
    const asterisks = (asterisksByDagger.get(code) ?? []).filter((a) => !relocated.has(a) && a !== primary.code);
    if (asterisks.length === 0) continue;
    clusterAsterisksByDagger.set(code, asterisks);
    asterisks.forEach((a) => relocated.add(a));
  }

  const segments: T[][] = [];
  const clustered = new Set<string>();
  for (const item of codesInSequence) {
    if (clustered.has(item.code)) {
      segments.push([item]);
    } else if (item.code === primary.code) {
      clustered.add(item.code);
      segments.push(primaryCluster);
    } else if (relocated.has(item.code)) {
    } else if (clusterAsterisksByDagger.has(item.code)) {
      clustered.add(item.code);
      segments.push(buildClusterAfter(item, clusterAsterisksByDagger.get(item.code)!));
    } else {
      segments.push([item]);
    }
  }

  const glued = segments.flat();
  const lostCodes = findLostCodes(codesInSequence, glued);
  if (lostCodes.length > 0) return { codesInSequence: [...codesInSequence], orphanedAsterisks, lostCodes };

  return { codesInSequence: glued, orphanedAsterisks };
};
